<script setup lang="ts" generic="T extends Record<string, any>">
import { OverlayScrollbars } from "overlayscrollbars";
import { nextTick, onMounted, onUnmounted, ref } from "vue";
import { DynamicScroller } from "vue-virtual-scroller";

interface ScrollerElementWithDescriptors extends HTMLElement {
  _originalScrollDescriptors?: {
    scrollTop: PropertyDescriptor | undefined;
    scrollHeight: PropertyDescriptor | undefined;
  };
}

interface DynamicScrollerWithScrollbarOverlayExposed {
  scroller: InstanceType<typeof DynamicScroller> | null;
  $el?: HTMLElement;
  getItemOffset: (index: number) => number;
  setScrollTop: (top: number) => void;
  scrollToItem: (index: number) => void;
}

withDefaults(
  defineProps<{
    items: T[];
    minItemSize: number;
    keyField: string;
    extraOverscroll?: boolean;
    scrollParent?: HTMLElement | null;
  }>(),
  {
    extraOverscroll: false,
    scrollParent: null,
  }
);

defineEmits<{
  click: [ev: MouseEvent];
  keydown: [ev: KeyboardEvent];
  scroll: [ev: Event];
  dragover: [ev: DragEvent];
  drop: [ev: DragEvent];
}>();

const scroller = ref<InstanceType<typeof DynamicScroller> | null>(null);
let overlayScrollbarsInstance: OverlayScrollbars | null = null;
let scrollEventHandler: (() => void) | null = null;
let intentionalScrollReset = false;

onMounted(async () => {
  await nextTick();

  if (!scroller.value?.$el) {
    return;
  }

  const scrollerElement = scroller.value.$el;

  overlayScrollbarsInstance = OverlayScrollbars(scrollerElement, {
    overflow: { x: "hidden" },
    scrollbars: {
      autoHide: "leave",
      autoHideDelay: 800,
    },
  });

  const { viewport } = overlayScrollbarsInstance.elements();

  if (!viewport) {
    return;
  }

  // Store original property descriptors
  const originalScrollTop = Object.getOwnPropertyDescriptor(scrollerElement, "scrollTop");
  const originalScrollHeight = Object.getOwnPropertyDescriptor(scrollerElement, "scrollHeight");

  let isSyncing = false;

  // DynamicScroller and OverlayScrollbars both manage scroll state.
  // We intercept scrollTop to prevent DynamicScroller from resetting scroll position
  // during virtual list updates.
  Object.defineProperty(scrollerElement, "scrollTop", {
    get: () => viewport.scrollTop,
    set: (value) => {
      if (!isSyncing) {
        isSyncing = true;
        const currentScroll = viewport.scrollTop;

        const isUnintentionalReset = value === 0 && currentScroll > 0 && !intentionalScrollReset;

        if (isUnintentionalReset) {
          isSyncing = false;
          return;
        }

        const scrollDelta = Math.abs(currentScroll - value);
        if (scrollDelta > 1) {
          viewport.scrollTop = value;
        }

        intentionalScrollReset = false;
        isSyncing = false;
      }
    },
    configurable: true,
  });

  Object.defineProperty(scrollerElement, "scrollHeight", {
    get: () => viewport.scrollHeight,
    set: () => undefined,
    configurable: true,
  });

  Object.defineProperty(scrollerElement, "scrollTo", {
    value: viewport.scrollTo.bind(viewport),
    configurable: true,
  });

  let rafId: number | null = null;
  scrollEventHandler = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(() => {
      if (!isSyncing) {
        isSyncing = true;
        const scrollEvent = new Event("scroll", {
          bubbles: true,
          cancelable: false,
        });
        scrollerElement.dispatchEvent(scrollEvent);
        isSyncing = false;
      }
      rafId = null;
    });
  };

  viewport.addEventListener("scroll", scrollEventHandler, { passive: true });

  (scrollerElement as ScrollerElementWithDescriptors)._originalScrollDescriptors = {
    scrollTop: originalScrollTop,
    scrollHeight: originalScrollHeight,
  };
});

onUnmounted(() => {
  if (scrollEventHandler && overlayScrollbarsInstance) {
    const { viewport } = overlayScrollbarsInstance.elements();
    viewport?.removeEventListener("scroll", scrollEventHandler);
    scrollEventHandler = null;

    const scrollerElement = scroller.value?.$el as ScrollerElementWithDescriptors | undefined;
    const descriptors = scrollerElement?._originalScrollDescriptors;
    if (scrollerElement && descriptors) {
      if (descriptors.scrollTop) {
        Object.defineProperty(scrollerElement, "scrollTop", descriptors.scrollTop);
      }
      if (descriptors.scrollHeight) {
        Object.defineProperty(scrollerElement, "scrollHeight", descriptors.scrollHeight);
      }
      delete scrollerElement._originalScrollDescriptors;
    }
  }

  overlayScrollbarsInstance?.destroy();
  overlayScrollbarsInstance = null;
});

defineExpose<DynamicScrollerWithScrollbarOverlayExposed>({
  scroller,
  get $el() {
    return scroller.value?.$el;
  },
  getItemOffset(index: number) {
    return scroller.value?.getItemOffset(index) ?? 0;
  },
  setScrollTop(top: number) {
    const el = scroller.value?.$el;
    if (!el) {
      return;
    }

    intentionalScrollReset = true;
    el.scrollTop = top;
  },
  scrollToItem(index: number) {
    if (index === 0) {
      intentionalScrollReset = true;
    }
    scroller.value?.scrollToItem(index);
  },
});
</script>

<template>
  <DynamicScroller
    ref="scroller"
    :items="items"
    :min-item-size="minItemSize"
    :key-field="keyField"
    :scroll-parent="scrollParent"
    :page-mode="Boolean(scrollParent)"
    class="h-full overflow-x-hidden overflow-y-auto"
    :class="extraOverscroll ? 'pb-[300px]' : ''"
    @click="$emit('click', $event)"
    @keydown="$emit('keydown', $event)"
    @scroll="$emit('scroll', $event)"
    @dragover="$emit('dragover', $event)"
    @drop="$emit('drop', $event)">
    <template #default="slotProps">
      <slot v-bind="slotProps" />
    </template>
    <template #before>
      <slot name="before" />
    </template>
    <template #after>
      <slot name="after" />
    </template>
  </DynamicScroller>
</template>
